import {
  BrowserAuthError,
  ClientAuthError,
  InteractionRequiredAuthError,
  ServerError,
} from '@azure/msal-browser';
import {
  AdminConsentRequiredError,
  getAuthenticationScopes,
  handleError,
  initGraphContainer,
  logError,
  logInfo,
  PopupAuthError,
  TokenAcquisitionError,
} from '@utils-global';
import { AuthMethod } from '../../app/auth/authMethod';
import { OfficeAccountManager } from '../../app/auth/naa/accountManager';
import { useAuthStorage } from '../storages';

/**
 * Handles CAE (Continuous Access Evaluation) challenges by refreshing the token with claims
 * @param claims - The claims challenge from the WWW-Authenticate header
 * @param isSharedMailbox - Whether the user is accessing a shared mailbox
 * @returns New access token or null if refresh failed
 */
export const handleCaeChallenge = async (
  claims: string,
  isSharedMailbox: boolean,
): Promise<string | null> => {
  logInfo('Handling CAE challenge, attempting token refresh with claims', { claims });

  try {
    const accountManager = new OfficeAccountManager();
    await accountManager.initialize();

    const scopes = getAuthenticationScopes(isSharedMailbox);
    const { accessToken, idToken } = await accountManager.ssoGetUserIdentity(scopes, true, claims);

    useAuthStorage
      .getState()
      .setTokens({ graphToken: accessToken, idToken }, AuthMethod.NAA_INTERACTIVE);

    // Reinitialize Graph client with new token
    initGraphContainer(accessToken);

    logInfo('CAE challenge resolved - token refreshed successfully');
    return accessToken;
  } catch (error) {
    logError('Failed to refresh token with CAE claims challenge', error);

    const isExpectedAuthError =
      error instanceof PopupAuthError ||
      error instanceof AdminConsentRequiredError ||
      error instanceof BrowserAuthError ||
      error instanceof ClientAuthError ||
      error instanceof ServerError ||
      error instanceof InteractionRequiredAuthError ||
      error instanceof TokenAcquisitionError;

    if (!isExpectedAuthError) {
      handleError(error);
    }

    return null;
  }
};
